/**
 * System-wide enums used by both the API layer and database models.
 *
 * These are const objects rather than TypeScript `enum` so that they tree-shake
 * cleanly and play nicely with Zod's `z.nativeEnum()` helper.
 */

export const USER_STATUS = {
  PENDING: 'pending', // awaiting email verification
  ACTIVE: 'active', // normal user
  LOCKED: 'locked', // admin-locked (cannot login)
  FROZEN: 'frozen', // locked + all balance ops disabled
  BANNED: 'banned', // soft-deleted, cannot be un-banned via UI
} as const;
export type UserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS];

export const LEDGER_ACTION = {
  RECHARGE: 'recharge', // user deposit
  WITHDRAW: 'withdraw', // user withdrawal
  TRANSFER_IN: 'transfer_in', // P2P received
  TRANSFER_OUT: 'transfer_out', // P2P sent
  DAILY_SETTLEMENT: 'daily_settlement', // legacy ledger only (job removed)
  REFERRAL_BONUS: 'referral_bonus', // upline reward
  LEVEL_BONUS: 'level_bonus', // level-up bonus
  FORK_EVENT: 'fork_event', // fork distribution
  EP_TRADE: 'ep_trade', // EP marketplace matched
  ADMIN_ADJUST: 'admin_adjust', // manual correction
  SYSTEM_CORRECTION: 'system_correction', // reversal / bug fix
  SPLIT_POOL_IN: 'split_pool_in',
  SPLIT_POOL_OUT: 'split_pool_out',
  RECHARGE_FEE: 'recharge_fee',
  WITHDRAW_FEE: 'withdraw_fee',
  /** Same-user bucket swap — debit leg */
  INTERNAL_CONVERT_OUT: 'internal_convert_out',
  /** Same-user bucket swap — credit leg */
  INTERNAL_CONVERT_IN: 'internal_convert_in',
  /** Charity donation — debit leg. Credited cause aggregate, no system credit. */
  DONATION: 'donation',
  /** PCC card purchase — debit leg. User spends UO to acquire a PCC holding. */
  PCC_PURCHASE: 'pcc_purchase',
  /** PCC card earnings — credit leg. Settlement payouts to holding owners. */
  PCC_EARNINGS: 'pcc_earnings',
  /** UOC mint — EP1/EP2 converted into UOC (credit leg). */
  UOC_MINT: 'uoc_mint',
  /** UOC burn — UOC converted out to RP/TP/EP (debit leg). */
  UOC_BURN: 'uoc_burn',
  /** UOC admin recharge — admin adds UOC supply to the system pool. */
  UOC_ADMIN_RECHARGE: 'uoc_admin_recharge',
  /** Activation code distribution — admin credits user with activation tokens. */
  ACTIVATION_DISTRIBUTE: 'activation_distribute',
  /** Activation code redeem — user consumes activation balance to activate account. */
  ACTIVATION_REDEEM: 'activation_redeem',
  /** Admin credits user (§2.14 给用户转币 / legacy uo.user/activation_to, uoc_to). */
  ADMIN_TRANSFER_COIN: 'admin_transfer_coin',
  STATIC_REWARD_EP1: 'static_reward_ep1',
  STATIC_REWARD_SP1: 'static_reward_sp1',
  STATIC_REWARD_EP2: 'static_reward_ep2',
  STATIC_REWARD_SP2: 'static_reward_sp2',
  STATIC_REWARD_RP: 'static_reward_rp',
  STATIC_REWARD_FC: 'static_reward_fc',
  PROMOTION_DONATION_UO: 'promotion_donation_uo',
  PROMOTION_DONATION_FP: 'promotion_donation_fp',
  LEVEL_PROMOTION_BONUS: 'level_promotion_bonus',
  WITHDRAW_SPLIT_CREDIT: 'withdraw_split_credit',
  WITHDRAW_SPLIT_FEE: 'withdraw_split_fee',
  WITHDRAW_SPLIT_BURN: 'withdraw_split_burn',
  /** UOC 转出 — sender debited when entering queue */
  UOC_TRANSFER_OUT: 'uoc_transfer_out',
  /** UOC 转出 — recipient credited when queue processed */
  UOC_TRANSFER_IN: 'uoc_transfer_in',
  /** UOC 转出 rejected — refund to sender */
  UOC_TRANSFER_REFUND: 'uoc_transfer_refund',
  /** AP兑捐UO — FP debit */
  AP_DONATE_FP: 'ap_donate_fp',
  /** AP兑捐UO — UO credit */
  AP_DONATE_UO: 'ap_donate_uo',
  /** §9 主号开通扣减 */
  MAIN_ACTIVATION_SP2: 'main_activation_sp2',
  MAIN_ACTIVATION_TP: 'main_activation_tp',
  MAIN_ACTIVATION_FC: 'main_activation_fc',
  /** Master opens a sub-account — RP debit (legacy RP payment path) */
  SUB_ACCOUNT_OPEN_RP: 'sub_account_open_rp',
  /** Master opens a sub-account — TP debit (10% split for SP3/RP) */
  SUB_ACCOUNT_OPEN_TP: 'sub_account_open_tp',
  /** Master opens a sub-account — SP1 debit (100% of principal) */
  SUB_ACCOUNT_OPEN_SP1: 'sub_account_open_sp1',
  /** Master opens a sub-account — SP2 debit (100% of principal) */
  SUB_ACCOUNT_OPEN_SP2: 'sub_account_open_sp2',
  /** Master opens a sub-account — SP3 debit (90% of principal, +10% TP) */
  SUB_ACCOUNT_OPEN_SP3: 'sub_account_open_sp3',
  /** Master opens a sub-account — flat FC fee (all payment methods) */
  SUB_ACCOUNT_OPEN_FC: 'sub_account_open_fc',
  /** Sponsor registers a new downline member — RP debit from sponsor */
  REGISTER_MEMBER_RP: 'register_member_rp',
  /** Sponsor registers a new downline member — TP debit from sponsor */
  REGISTER_MEMBER_TP: 'register_member_tp',
  /** New member receives UO from sponsor registration package */
  REGISTER_MEMBER_UO_GRANT: 'register_member_uo_grant',
  /** AP credited while member waits in legacy buy queue (type=1). */
  REGISTER_MEMBER_AP: 'register_member_ap',
  /** UO from register hang-buy match (integer leg). */
  REGISTER_MEMBER_HANG_UO: 'register_member_hang_uo',
  /** FP from register hang-buy match (fractional UO leg). */
  REGISTER_MEMBER_HANG_FP: 'register_member_hang_fp',
  /** @deprecated sponsor paid UO — use REGISTER_MEMBER_RP + REGISTER_MEMBER_TP */
  REGISTER_MEMBER_UO: 'register_member_uo',
  /** Sponsor registers a new downline member — flat FC fee (0.05) */
  REGISTER_MEMBER_FC: 'register_member_fc',
  /** @deprecated legacy EP debit */
  REGISTER_MEMBER_EP: 'register_member_ep',
  /** TP 充值入账 */
  RECHARGE_TP: 'recharge_tp',
  /** RP 充值入账（链上 USDT） */
  RECHARGE_RP: 'recharge_rp',
  /** FC 燃料购买入账（链上 USDT，13 USDT/FC 可调） */
  RECHARGE_FC: 'recharge_fc',
  /** TP 血缘转出手续费 — 入平台 TP 池 */
  LINEAGE_TP_FEE: 'lineage_tp_fee',
  /** 平衡奖 §5.2 */
  BALANCE_BONUS: 'balance_bonus',
  /** 分享奖级差 §5.2.1 */
  SHARE_BONUS: 'share_bonus',
  /** 平级辅导 5 EP §5.2 */
  PEER_LEVEL_BONUS: 'peer_level_bonus',
  /** 辅导奖 §5.2 */
  MENTORSHIP_BONUS: 'mentorship_bonus',
  /** OTC order collateral lock (UO or AP escrow) */
  OTC_LOCK: 'otc_lock',
  /** OTC matched trade settlement */
  OTC_SETTLE: 'otc_settle',
  /** OTC trading fee destroyed from EP */
  OTC_FEE_EP: 'otc_fee_ep',
  /** OTC cancelled order — collateral returned */
  OTC_REFUND: 'otc_refund',
  /** MP → ORB — MP debit */
  MP_CONVERT_OUT: 'mp_convert_out',
  /** MP → ORB — ORB credit */
  MP_CONVERT_UO: 'mp_convert_uo',
} as const;
export type LedgerAction = (typeof LEDGER_ACTION)[keyof typeof LEDGER_ACTION];

export const EP_ORDER_STATUS = {
  OPEN: 'open', // posted, waiting buyer
  CLAIMED: 'claimed', // buyer claimed, seller's EP locked in escrow
  PAID: 'paid', // buyer marked fiat as sent
  DONE: 'done', // seller released EP — complete
  CANCELLED: 'cancelled', // user/admin/system cancelled before claim
  DISPUTED: 'disputed', // escalated by either party
  TIMEOUT: 'timeout', // auto-cancelled by scheduler
} as const;
export type EpOrderStatus = (typeof EP_ORDER_STATUS)[keyof typeof EP_ORDER_STATUS];

export const RECHARGE_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
} as const;
export type RechargeStatus = (typeof RECHARGE_STATUS)[keyof typeof RECHARGE_STATUS];

export const WITHDRAW_STATUS = {
  /** Just created — UO is locked in escrow via LedgerService. */
  PENDING: 'pending',
  /** Admin approved — off-chain payout queued/executed. */
  APPROVED: 'approved',
  /** Admin rejected — locked UO refunded to user balance. */
  REJECTED: 'rejected',
} as const;
export type WithdrawStatus = (typeof WITHDRAW_STATUS)[keyof typeof WITHDRAW_STATUS];

export const WITHDRAW_SPLIT_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
} as const;
export type WithdrawSplitStatus =
  (typeof WITHDRAW_SPLIT_STATUS)[keyof typeof WITHDRAW_SPLIT_STATUS];

export const UOC_TRANSFER_SPLIT_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
} as const;
export type UocTransferSplitStatus =
  (typeof UOC_TRANSFER_SPLIT_STATUS)[keyof typeof UOC_TRANSFER_SPLIT_STATUS];

export const ADMIN_ROLE_PRESET = {
  SUPER: 'super', // full access, cannot be deleted
  FINANCE: 'finance', // recharge/withdraw/EP review
  OPERATOR: 'operator', // day-to-day customer ops
  VIEWER: 'viewer', // read-only
} as const;
export type AdminRolePreset = (typeof ADMIN_ROLE_PRESET)[keyof typeof ADMIN_ROLE_PRESET];
